import { Injectable } from '@angular/core';
import {
  concat,
  exhaustMap,
  Observable,
  of,
  takeWhile,
  tap,
  timeout,
  timer,
} from 'rxjs';
import {
  DataConnection,
  DataConnectionType,
} from '@nimmsta/ng-commons-lib/models';
import { WorkflowDataService } from '@nimmsta/ng-commons-lib/data-access';
import { EndpointResponse } from '../models/endpoint-response';
import {
  isSortingImportPending,
  SortingImportResponse,
  SortingUploadRequest,
} from '../models/sortingDataConnection';

@Injectable({ providedIn: 'root' })
export class DataConnectionService extends WorkflowDataService<DataConnection> {
  // Retain progress across dialog closes without retaining uploaded customer data.
  private readonly sortingImports = new Map<string, SortingImportResponse>();

  protected override get apiEndpoint(): string {
    return '/dataconnection';
  }

  simpleList(): Observable<DataConnection[]> {
    return this.http.get<DataConnection[]>(
      `${this.baseUrl + this.apiEndpoint}/simple`,
    );
  }

  getAllDataConnectionsSupported(): Observable<DataConnectionType[]> {
    return this.http.get<DataConnectionType[]>(
      `${this.baseUrl}/workflow/info/data-connections`,
    );
  }

  execute<T = string>(
    dataConnection: Pick<DataConnection, 'id'>,
    body = {},
  ): Observable<T> {
    return this.http.post<T>(
      `${this.baseUrl + this.apiEndpoint}/${dataConnection.id}/execute`,
      body,
    );
  }

  uploadSortingData(
    connectionId: string,
    request: SortingUploadRequest,
  ): Observable<SortingImportResponse> {
    return this.execute<SortingImportResponse>(
      { id: connectionId },
      request,
    ).pipe(tap((status) => this.sortingImports.set(connectionId, status)));
  }

  lastSortingImport(connectionId: string): SortingImportResponse | undefined {
    return this.sortingImports.get(connectionId);
  }

  getSortingImportStatus(
    connectionId: string,
    importId: string,
  ): Observable<SortingImportResponse> {
    return this.http
      .get<SortingImportResponse>(
        `${this.baseUrl + this.apiEndpoint}/${connectionId}/imports/${importId}`,
      )
      .pipe(
        timeout(30_000),
        tap((status) => this.sortingImports.set(connectionId, status)),
      );
  }

  watchSortingImport(
    connectionId: string,
    initial: SortingImportResponse,
  ): Observable<SortingImportResponse> {
    if (!isSortingImportPending(initial.status)) {
      return of(initial);
    }
    return concat(
      of(initial),
      timer(2000, 2000).pipe(
        exhaustMap(() => this.getSortingImportStatus(connectionId, initial.id)),
        takeWhile((status) => isSortingImportPending(status.status), true),
      ),
    );
  }

  validate(dataConnectionHandlerName: string, body = {}): Observable<string> {
    return this.http.post<string>(
      `${this.baseUrl}/workflow/info/data-connection/${dataConnectionHandlerName}/validate`,
      body,
    );
  }

  executeEndpoint(
    dataConnectionIdentifier: string,
    endpointIdentifier: string,
    request: unknown = {},
  ): Observable<string> {
    return this.http.post(
      `${this.baseUrl}/dataconnection/${dataConnectionIdentifier}/${endpointIdentifier}/execute`,
      request,
      {
        responseType: 'text',
      },
    );
  }

  executeEndpointWithResponse(
    dataConnectionIdentifier: string,
    endpointIdentifier: string,
    request: unknown = {},
  ): Observable<EndpointResponse> {
    return this.http.post(
      `${this.baseUrl}/dataconnection/${dataConnectionIdentifier}/${endpointIdentifier}/executeWithResponse`,
      request,
      {
        responseType: 'json',
      },
    ) as Observable<EndpointResponse>;
  }
}
